import React, { useEffect, useState, createElement } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, ShieldCheck, Lock, ArrowRight, X, Rocket, Download, Smartphone, Target } from 'lucide-react';
const Modal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose} data-id="element-0">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-1">
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200" data-id="element-2">
          <h2 className="font-serif text-2xl font-bold text-brand-dark" data-id="element-3">
            {title}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close" data-id="element-4">
            <X className="w-6 h-6 text-gray-500" data-id="element-5" />
          </button>
        </div>
        <div className="px-8 py-6 overflow-y-auto max-h-[70vh] text-gray-700 text-base leading-relaxed space-y-4" data-id="element-6">
          {children}
        </div>
      </div>
    </div>;
};
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-300 last:border-0" data-id="element-7">
      <button onClick={() => setIsOpen(!isOpen)} className="flex justify-between items-center w-full py-5 text-left focus:outline-none" aria-expanded={isOpen} data-id="element-8">
        <span className="font-serif text-xl md:text-2xl font-bold text-brand-dark pr-4" data-id="element-9">
          {question}
        </span>
        {isOpen ? <ChevronUp className="w-6 h-6 text-brand-gold flex-shrink-0" data-id="element-10" /> : <ChevronDown className="w-6 h-6 text-brand-gold flex-shrink-0" data-id="element-11" />}
      </button>
      {isOpen && <div className="pb-5 text-lg md:text-xl leading-relaxed text-gray-700" data-id="element-12">
          {answer}
        </div>}
    </div>;
};
export function App() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  useEffect(() => {
    document.title = 'The Senior Benefit Unlocker';
    const setMeta = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (name.startsWith('og:')) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    setMeta('og:title', 'The Senior Benefit Unlocker');
    setMeta('og:description', 'Reclaim the $400 to $1,200 in monthly credits the system owes you.');
    setMeta('description', 'Reclaim the $400 to $1,200 in monthly credits the system owes you.');
  }, []);
  return <div className="font-sans text-gray-800 bg-white selection:bg-brand-gold selection:text-brand-dark" data-id="element-13">
      {/* ===== 1. HERO SECTION ===== */}
      <section className="bg-brand-dark text-white pt-16 pb-20 md:pt-24 md:pb-28 px-6" data-id="element-14">
        <div className="max-w-6xl mx-auto" data-id="element-15">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16" data-id="element-16">
            <div className="flex-1 text-center lg:text-left" data-id="element-17">
              <div className="inline-block px-4 py-1.5 mb-6 text-sm md:text-base font-bold tracking-wider text-brand-dark bg-brand-gold rounded-full uppercase" data-id="element-18">
                New Release for 2026
              </div>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-id="element-19">
                The Senior Benefit Unlocker:{' '}
                <span className="text-brand-gold" data-id="element-20">
                  Your Roadmap to $1,200/mo
                </span>
              </h1>
              <h2 className="text-xl md:text-2xl font-medium leading-relaxed text-gray-300 mb-4" data-id="element-21">
                Stop choosing between groceries and electricity—reclaim the{' '}
                <span className="text-brand-gold font-bold" data-id="element-22">
                  $400 to $1,200
                </span>{' '}
                in monthly credits the system owes you.
              </h2>
              <p className="text-lg text-gray-400 mb-10" data-id="element-23">
                Beginner-friendly. No experience needed.
              </p>
              <a href="#checkout" className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-5 text-xl font-bold text-brand-dark bg-brand-gold hover:bg-brand-gold-hover transition-colors rounded-lg shadow-lg" data-id="element-24">
                Get Instant Download
                <ArrowRight className="ml-3 w-6 h-6" data-id="element-25" />
              </a>
              <div className="flex items-center mt-3 text-sm font-medium opacity-80 lg:justify-start justify-center" data-id="element-26">
                <Lock className="w-4 h-4 mr-1.5" data-id="element-27" />
                Secure checkout. Instant digital delivery.
              </div>
            </div>
            <div className="flex-1 w-full max-w-lg lg:max-w-none" data-id="element-28">
              <img src="https://cdn.magicpatterns.com/uploads/gpWjbD8KhtwUVrhi7qUaGc/The_Senior_Benefit_Unlocker_main_image.png" alt="Senior woman smiling while reading The Senior Benefit Unlocker on a tablet" className="w-full h-auto rounded-xl shadow-2xl border-4 border-brand-gold/20" data-id="element-29" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== 2. OPENING HOOK ===== */}
      <section className="bg-brand-cream py-20 md:py-28 px-6" data-id="element-30">
        <div className="max-w-3xl mx-auto text-lg md:text-xl leading-relaxed space-y-6" data-id="element-31">
          <p className="font-serif text-2xl md:text-3xl font-bold text-brand-dark leading-snug mb-8" data-id="element-32">
            You worked for forty years. You paid your taxes, raised your family,
            and played by the rules.
          </p>
          <p data-id="element-33">
            You were promised a "golden age" of security, but instead, you're
            sitting at the kitchen table at 11 PM wondering how $42 is supposed
            to last nine more days.
          </p>
          <p data-id="element-34">
            It feels like a knot in your chest that never goes away. You're
            scanning the grocery aisles for the cheapest eggs and keeping the
            heat at 65 degrees just to survive.
          </p>
        </div>
      </section>

      {/* ===== 3. THE PROBLEM ===== */}
      <section className="bg-white py-20 md:py-28 px-6" data-id="element-35">
        <div className="max-w-3xl mx-auto text-lg md:text-xl leading-relaxed space-y-6" data-id="element-36">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-8 text-center" data-id="element-37">
            The System Isn't Broken—It's Designed to Be a Maze
          </h2>
          <p data-id="element-38">
            The truth is, inflation has turned your fixed Social Security check
            into a "pay cut." While prices for milk, meds, and power have
            skyrocketed, your income has stayed nearly flat.
          </p>
          <p className="font-medium text-brand-dark p-6 bg-gray-50 border-l-4 border-brand-gold rounded-r-lg mt-8" data-id="element-39">
            The system isn't broken—it's designed to be a maze. It's built to be
            so frustrating that most seniors simply give up and stop asking for
            the benefits they already paid for.
          </p>
        </div>
      </section>

      {/* ===== 4. THE UNIQUE MECHANISM ===== */}
      <section className="bg-brand-dark text-white py-20 md:py-28 px-6" data-id="element-40">
        <div className="max-w-3xl mx-auto text-lg md:text-xl leading-relaxed space-y-8 text-center" data-id="element-41">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-gold mb-6" data-id="element-42">
            The "State-Federal Benefit Gap" Audit
          </h2>
          <p className="text-gray-300" data-id="element-43">
            A targeted framework for{' '}
            <strong className="text-white" data-id="element-44">beginners 60+</strong>. It ignores
            the "front door" and identifies specific, non-taxable
            "Micro-Credits" at the state level that stack together.
          </p>
          <p className="text-gray-300" data-id="element-45">
            Unlock hundreds of dollars in food, utility, and medical premium
            relief—
            <strong className="text-white" data-id="element-46">
              giving yourself a 20% to 30% raise on your Social Security check
              overnight.
            </strong>
          </p>
        </div>
      </section>

      {/* ===== 5. VALUE STACK — WHAT'S INSIDE ===== */}
      <section className="bg-brand-cream py-20 md:py-28 px-6" data-id="element-47">
        <div className="max-w-4xl mx-auto" data-id="element-48">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-4 text-center" data-id="element-49">
            📦 Here Is What's Inside
          </h2>
          <p className="text-center text-lg text-gray-500 mb-12" data-id="element-50">
            Everything you need to start reclaiming your benefits today.
          </p>
          <div className="space-y-4" data-id="element-51">
            {[{
            title: 'The Full "Senior Benefit Unlocker" Guide (35+ Page PDF)',
            desc: 'Your complete step-by-step manual to unlocking hidden benefits.'
          }, {
            title: 'The "Invisible Check" Roadmap',
            desc: 'Accessing the $400–$1,200 in monthly credits waiting for you.'
          }, {
            title: 'The LIHEAP Utility Hack',
            desc: 'Get your utility bills paid & your home sealed for $0.'
          }, {
            title: 'The Medicare Part B $174.70 Refund',
            desc: 'Put your premium back in your check every single month.'
          }, {
            title: 'The "Bureaucracy Scripts"',
            desc: 'Exactly what to say on the phone to get a "Yes" from state agencies.'
          }, {
            title: '15-Minute Benefit Audit Worksheet',
            desc: 'Calculate your new income in minutes.'
          }].map((item, i) => <div key={i} className="flex items-start p-6 bg-white rounded-xl shadow-sm border border-gray-100" data-id="element-52">
                <CheckCircle2 className="w-8 h-8 text-brand-gold flex-shrink-0 mr-4 mt-0.5" data-id="element-53" />
                <div data-id="element-54">
                  <h3 className="font-bold text-xl text-brand-dark mb-1" data-id="element-55">
                    {item.title}
                  </h3>
                  <p className="text-lg text-gray-700" data-id="element-56">{item.desc}</p>
                </div>
              </div>)}
          </div>

          {/* Badges */}
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 mt-12" data-id="element-57">
            <div className="flex items-center gap-2 bg-white px-5 py-3 rounded-full shadow-sm border border-gray-100" data-id="element-58">
              <Download className="w-5 h-5 text-brand-gold" data-id="element-59" />
              <span className="font-bold text-sm uppercase tracking-wider text-brand-dark" data-id="element-60">
                Instant Download
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white px-5 py-3 rounded-full shadow-sm border border-gray-100" data-id="element-61">
              <Smartphone className="w-5 h-5 text-brand-gold" data-id="element-62" />
              <span className="font-bold text-sm uppercase tracking-wider text-brand-dark" data-id="element-63">
                Phone / Tablet / Computer
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white px-5 py-3 rounded-full shadow-sm border border-gray-100" data-id="element-64">
              <Target className="w-5 h-5 text-brand-gold" data-id="element-65" />
              <span className="font-bold text-sm uppercase tracking-wider text-brand-dark" data-id="element-66">
                Beginner-Friendly
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 6. WHO THIS IS FOR ===== */}
      <section className="bg-white py-20 md:py-28 px-6" data-id="element-67">
        <div className="max-w-3xl mx-auto" data-id="element-68">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-12 text-center" data-id="element-69">
            Who This Is For
          </h2>
          <div className="bg-brand-cream p-8 md:p-10 rounded-2xl border-t-8 border-brand-gold" data-id="element-70">
            <ul className="space-y-6" data-id="element-71">
              {['US Retirees 60+ living on a fixed Social Security income.', 'Seniors who feel they "make too much" for help but still struggle.', 'Homeowners 62+ worried about rising property taxes.'].map((text, i) => <li key={i} className="flex items-start" data-id="element-72">
                  <CheckCircle2 className="w-7 h-7 text-green-600 flex-shrink-0 mr-4 mt-0.5" data-id="element-73" />
                  <span className="text-lg md:text-xl text-gray-700" data-id="element-74">
                    {text}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* ===== 7. PROOF / TESTIMONIAL ===== */}
      <section className="bg-brand-dark py-20 md:py-28 px-6" data-id="element-75">
        <div className="max-w-3xl mx-auto text-center" data-id="element-76">
          <div className="text-5xl mb-6" data-id="element-77">💬</div>
          <blockquote className="font-serif text-2xl md:text-3xl font-bold text-white leading-relaxed mb-8" data-id="element-78">
            "I didn't think I qualified for anything. After 30 minutes with this
            guide, I found a $174 monthly Medicare refund I never knew existed.
            My fridge is finally full again."
          </blockquote>
          <p className="text-brand-gold text-lg font-bold" data-id="element-79">— Margaret S., 68</p>
          <p className="text-gray-400 text-sm mt-2" data-id="element-80">One of 4,200+ Readers</p>
        </div>
      </section>

      {/* ===== 8. OFFER + PRICE BLOCK & CTA ===== */}
      <section id="checkout" className="bg-brand-cream py-20 md:py-28 px-6" data-id="element-81">
        <div className="max-w-3xl mx-auto text-center" data-id="element-82">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-dark mb-4" data-id="element-83">
            💰 Get the Raise You Earned
          </h2>
          <p className="text-xl md:text-2xl text-gray-700 mb-10 leading-relaxed" data-id="element-84">
            Learn the "Invisible Check" Method in 60 minutes.
          </p>

          <div className="bg-white p-10 rounded-3xl border-2 border-brand-gold max-w-xl mx-auto mb-10 shadow-lg" data-id="element-85">
            <div className="mb-2" data-id="element-86">
              <span className="text-2xl md:text-3xl font-bold text-gray-400 line-through" data-id="element-87">
                $19.00
              </span>
            </div>
            <div className="text-base font-bold text-red-600 uppercase tracking-wider mb-3" data-id="element-88">
              Limited-Time Offer While Available
            </div>
            <div className="text-6xl md:text-7xl font-bold text-brand-dark mb-2" data-id="element-89">
              $6.39
            </div>
            <p className="text-gray-500 text-sm mb-8" data-id="element-90">
              One-time payment. Instant access.
            </p>

            <a href="https://www.shopdilore.com/cart/53835577721142:1" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-full px-8 py-5 text-xl font-bold text-brand-dark bg-brand-gold hover:bg-brand-gold-hover transition-colors rounded-lg shadow-lg" data-id="element-91">
              🚀 GET INSTANT ACCESS — $6.39
              <ArrowRight className="ml-3 w-6 h-6" data-id="element-92" />
            </a>
            <div className="flex items-center justify-center mt-3 text-sm font-medium text-gray-500" data-id="element-93">
              <Lock className="w-4 h-4 mr-1.5" data-id="element-94" />
              Secure checkout. Instant digital delivery.
            </div>
          </div>
        </div>
      </section>

      {/* ===== 9. GUARANTEE ===== */}
      <section className="bg-white py-16 md:py-20 px-6" data-id="element-95">
        <div className="max-w-3xl mx-auto text-center" data-id="element-96">
          <ShieldCheck className="w-16 h-16 text-brand-gold mx-auto mb-6" data-id="element-97" />
          <h3 className="font-serif text-2xl md:text-3xl font-bold text-brand-dark mb-4" data-id="element-98">
            🛡️ 30-Day Peace of Mind Guarantee
          </h3>
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-2xl mx-auto" data-id="element-99">
            If you don't find at least one credit you qualify for, or if you
            aren't 100% satisfied, email us for a full, no-questions-asked
            refund.{' '}
            <strong className="text-brand-dark" data-id="element-100">You keep the guide.</strong>
          </p>
        </div>
      </section>

      {/* ===== 10. FAQ LINK (opens popup) ===== */}
      <section className="bg-brand-cream py-12 px-6" data-id="element-101">
        <div className="max-w-3xl mx-auto text-center" data-id="element-102">
          <button onClick={() => setFaqOpen(true)} className="inline-flex items-center gap-2 text-xl font-bold text-brand-dark hover:text-brand-gold transition-colors underline underline-offset-4 decoration-brand-gold decoration-2" data-id="element-103">
            🖱️ QUICK FAQ
            <ChevronDown className="w-5 h-5" data-id="element-104" />
          </button>
        </div>
      </section>

      {/* ===== 11. FINAL CTA BANNER ===== */}
      <section className="bg-brand-gold py-10 px-6" data-id="element-105">
        <div className="max-w-3xl mx-auto text-center" data-id="element-106">
          <p className="text-xl md:text-2xl font-bold text-brand-dark" data-id="element-107">
            ✅ INSTANT DOWNLOAD AVAILABLE NOW
          </p>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-brand-dark text-gray-400 py-10 text-center text-sm px-6" data-id="element-108">
        <p data-id="element-109">
          © {new Date().getFullYear()} Dilore Publishing. All rights reserved.
        </p>
        <div className="mt-4 flex items-center justify-center gap-4" data-id="element-110">
          <button onClick={() => setPrivacyOpen(true)} className="underline underline-offset-2 hover:text-white transition-colors" data-id="element-111">
            Privacy Policy
          </button>
          <span className="w-1 h-1 rounded-full bg-gray-500" data-id="element-112"></span>
          <button onClick={() => setTermsOpen(true)} className="underline underline-offset-2 hover:text-white transition-colors" data-id="element-113">
            Terms of Service
          </button>
        </div>
        <p className="mt-4 max-w-2xl mx-auto opacity-60" data-id="element-114">
          Disclaimer: This guide provides educational information and is not
          legal or financial advice. Results vary based on individual
          circumstances and state-specific program funding.
        </p>
      </footer>

      {/* ===== FAQ MODAL ===== */}
      <Modal isOpen={faqOpen} onClose={() => setFaqOpen(false)} title="Quick FAQ" data-id="element-115">
        <FAQItem question="Is this legal?" answer="Yes, these are your tax-funded federal/state rights. Every program referenced in the guide is a legitimate government benefit." data-id="element-116" />
        <FAQItem question="Will it lower my Social Security?" answer="No, these credits are added on top of your retirement check. They do not reduce or interfere with your standard Social Security payouts." data-id="element-117" />
        <FAQItem question="Is it hard to do?" answer="No, we provide the exact scripts and checklists. If you can make a phone call and fill out a simple form, you can do this." data-id="element-118" />
        <FAQItem question="What's the format?" answer="Instant PDF download accessible on any device — phone, tablet, or computer." data-id="element-119" />
      </Modal>

      {/* ===== PRIVACY POLICY MODAL ===== */}
      <Modal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy Policy" data-id="element-120">
        <p data-id="element-121">
          <strong data-id="element-122">Last Updated:</strong> March 2026
        </p>
        <p data-id="element-123">
          Dilore Publishing ("we," "us," or "our") is committed to protecting
          your privacy. This Privacy Policy explains how we handle information
          when you purchase and use The Senior Benefit Unlocker guide.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-124">
          Information We Collect
        </h3>
        <p data-id="element-125">
          We collect only the minimum information necessary to process your
          purchase, including your email address and payment details. Payment
          processing is handled securely by our third-party payment processor —
          we never store your credit card information.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-126">
          How We Use Your Information
        </h3>
        <p data-id="element-127">
          Your email address is used solely to deliver your digital purchase and
          provide order-related communications. We do not sell, rent, or share
          your personal information with third parties for marketing purposes.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-128">
          Data Security
        </h3>
        <p data-id="element-129">
          We use industry-standard security measures to protect your
          information. All transactions are encrypted using SSL technology.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-130">Cookies</h3>
        <p data-id="element-131">
          Our website may use essential cookies to ensure proper functionality.
          We do not use tracking cookies for advertising purposes.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-132">Your Rights</h3>
        <p data-id="element-133">
          You may request access to, correction of, or deletion of your personal
          data at any time. This guide does not collect your Social Security
          number, medical records, or any government benefit information — it
          simply provides educational instructions for you to apply directly
          with government agencies.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-134">
          Changes to This Policy
        </h3>
        <p data-id="element-135">
          We may update this Privacy Policy from time to time. Any changes will
          be posted on this page with an updated revision date.
        </p>
      </Modal>

      {/* ===== TERMS OF SERVICE MODAL ===== */}
      <Modal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Terms of Service" data-id="element-136">
        <p data-id="element-137">
          <strong data-id="element-138">Last Updated:</strong> March 2026
        </p>
        <p data-id="element-139">
          By purchasing and using The Senior Benefit Unlocker ("the Guide"), you
          agree to the following terms and conditions.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-140">
          Product Description
        </h3>
        <p data-id="element-141">
          The Guide is a digital educational resource that provides information
          about publicly available government benefit programs. It is delivered
          electronically upon purchase.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-142">
          Educational Purpose Only
        </h3>
        <p data-id="element-143">
          The Guide is for informational and educational purposes only. It does
          not constitute legal, financial, or tax advice. We recommend
          consulting with a qualified professional regarding your specific
          situation. Eligibility for government programs varies by state,
          county, and individual circumstances.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-144">
          No Guarantees
        </h3>
        <p data-id="element-145">
          While the Guide provides actionable steps to identify potential
          benefits, we cannot guarantee specific dollar amounts or approval for
          any government program. Results vary based on individual eligibility,
          location, and program funding availability.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-146">
          Digital Delivery
        </h3>
        <p data-id="element-147">
          The Guide is a digital product delivered immediately upon purchase.
          Due to the nature of digital products, all sales are final. If you
          experience technical issues accessing your purchase, please reach out
          and we will ensure you receive your product.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-148">
          Intellectual Property
        </h3>
        <p data-id="element-149">
          All content within the Guide is the intellectual property of Dilore
          Publishing. You may not reproduce, distribute, or resell the Guide
          without written permission.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-150">
          Limitation of Liability
        </h3>
        <p data-id="element-151">
          Dilore Publishing shall not be held liable for any outcomes resulting
          from the use of information provided in the Guide. Your use of the
          Guide and any actions taken based on its contents are at your own
          discretion and risk.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-152">
          Governing Law
        </h3>
        <p data-id="element-153">
          These terms shall be governed by and construed in accordance with the
          laws of the United States.
        </p>
      </Modal>
    </div>;
}